import { getMediaBaseUrl, getSevenRoomsMediaBaseUrl } from "../../config/env";
import { formatAuditUserLabel } from "../../lib/listingSync.utils";
import {
	defaultFaqApplicable,
	parseFnBApplicableFor,
	parseFnBPropertyType,
} from "./fnb.propertyType";
import {
	FnBMenuType,
	FnBRestaurantSourceType,
	FnBVenueStatus,
} from "./fnb.types";
import type {
	RestaurantAddOn,
	RestaurantDetail,
	RestaurantFaqItem,
	RestaurantListItem,
	RestaurantMediaItem,
	RestaurantSeatingType,
	RestaurantSevenRoomsIntegration,
} from "./restaurant.types";

const FNB_MEDIA_BASE_URL = getMediaBaseUrl();

export function resolveFnbMediaUrl(path: string): string {
	if (!path) return "";
	if (path.startsWith("http://") || path.startsWith("https://")) return path;
	if (path.startsWith("blob:")) return path;
	return `${FNB_MEDIA_BASE_URL}/${path.replace(/^\/+/, "")}`;
}

function displayId(value: unknown): string {
	if (value == null || value === "") return "—";
	return String(value);
}

function mapSource(raw: unknown): RestaurantDetail["source"] {
	const v = String(raw ?? "").toUpperCase();
	return v === FnBRestaurantSourceType.SEVENROOMS
		? FnBRestaurantSourceType.SEVENROOMS
		: FnBRestaurantSourceType.MANUAL;
}

function mapPublicationStatus(raw: unknown): RestaurantDetail["status"] {
	const v = String(raw ?? "").toUpperCase();
	if (v === FnBVenueStatus.PUBLISHED) return FnBVenueStatus.PUBLISHED;
	if (v === FnBVenueStatus.INACTIVE) return FnBVenueStatus.INACTIVE;
	return FnBVenueStatus.DRAFT;
}

function isHttpUrl(value: string): boolean {
	return /^https?:\/\//i.test(value.trim());
}

function mapMenuFields(
	row: Record<string, unknown>,
): Pick<
	RestaurantDetail,
	"menuType" | "menuUrl" | "menuPdfNote" | "menuPdfKey" | "menuPdfViewUrl"
> {
	const webUrl = String(row.menuWebUrl ?? row.menuUrl ?? "").trim();
	const legacyPdf = String(row.menuPdfUrl ?? "").trim();
	const explicitPdf =
		String(row.menuType ?? "").toUpperCase() === FnBMenuType.PDF;
	const storageKey = !isHttpUrl(webUrl)
		? webUrl
		: !isHttpUrl(legacyPdf)
			? legacyPdf
			: "";
	if (explicitPdf || storageKey) {
		return {
			menuType: "PDF",
			menuUrl: undefined,
			menuPdfKey: storageKey || undefined,
			menuPdfNote: storageKey
				? storageKey.split("/").pop() || storageKey
				: undefined,
			menuPdfViewUrl: storageKey ? resolveFnbMediaUrl(storageKey) : undefined,
		};
	}
	return {
		menuType: "WEB",
		menuUrl: webUrl || undefined,
		menuPdfNote: undefined,
		menuPdfKey: undefined,
		menuPdfViewUrl: undefined,
	};
}

function mapMediaItems(
	bannerImage: unknown,
	mediaRaw: unknown,
	options?: { sevenRooms?: boolean },
): RestaurantMediaItem[] {
	const sevenRooms = options?.sevenRooms ?? false;
	const bannerPath =
		typeof bannerImage === "string" && bannerImage.trim()
			? bannerImage.trim()
			: "";
	const paths = Array.isArray(mediaRaw)
		? mediaRaw
				.filter(
					(p): p is string => typeof p === "string" && p.trim().length > 0,
				)
				.map((p) => p.trim())
		: [];

	const uniquePaths = [
		...(bannerPath ? [bannerPath] : []),
		...paths.filter((p) => p !== bannerPath),
	];

	return uniquePaths.map((path, index) => {
		const fileName = path.split("/").pop() ?? "media";
		return {
			id: `rmedia-${index}-${fileName}`,
			url: resolveIntegratedMediaUrl(path, sevenRooms),
			storageKey: path,
			fileName,
			sizeLabel: "",
			kind: path === bannerPath ? "hero" : "image",
		};
	});
}

function mapFaqs(
	raw: unknown,
	fallbackApplicable: RestaurantFaqItem["applicable"],
): RestaurantFaqItem[] {
	if (!Array.isArray(raw)) return [];
	return raw.map((item, index) => {
		const row = (item ?? {}) as Record<string, unknown>;
		const applicable =
			parseFnBApplicableFor(row.applicable) || fallbackApplicable;
		return {
			id: String(row.id ?? `rf-${index}`),
			questionHtml: String(row.question ?? ""),
			answerHtml: String(row.answer ?? ""),
			applicable,
		};
	});
}

function resolveIntegratedMediaUrl(path: string, sevenRooms: boolean): string {
	if (!path) return "";
	if (
		path.startsWith("http://") ||
		path.startsWith("https://") ||
		path.startsWith("blob:")
	) {
		return path;
	}
	if (sevenRooms) {
		return `${getSevenRoomsMediaBaseUrl()}/${path.replace(/^\/+/, "")}`;
	}
	return resolveFnbMediaUrl(path);
}

function mapIntegratedMediaPath(row: Record<string, unknown>): string {
	if (Array.isArray(row.media) && row.media.length > 0) {
		const first = row.media[0];
		if (typeof first === "string") return first.trim();
		if (first && typeof first === "object") {
			const media = first as Record<string, unknown>;
			return String(
				media.url ?? media.cdnUrl ?? media.imageUrl ?? media.filePath ?? "",
			).trim();
		}
	}
	return String(row.imageUrl ?? row.image ?? row.imagePath ?? "").trim();
}

function mapSeatingTypes(
	raw: unknown,
	options?: { sevenRooms?: boolean },
): RestaurantSeatingType[] {
	if (!Array.isArray(raw)) return [];
	const sevenRooms = options?.sevenRooms ?? false;
	return raw.map((item, index) => {
		if (typeof item === "string") {
			const name = item.trim();
			return {
				id: `st-${index}-${name}`,
				seatingId: `st-${index}-${name}`,
				name,
				descriptionHtml: "",
				imageUrl: "",
				applicable: "",
			};
		}
		const row = (item ?? {}) as Record<string, unknown>;
		const nested =
			row.seating && typeof row.seating === "object"
				? (row.seating as Record<string, unknown>)
				: {};
		const imagePath = mapIntegratedMediaPath(row);
		const seatingId = String(
			row.seatingId ?? nested.id ?? row.id ?? `st-${index}`,
		);
		return {
			id: String(row.id ?? seatingId),
			seatingId,
			name: String(row.name ?? row.title ?? nested.name ?? nested.title ?? ""),
			descriptionHtml: String(row.description ?? nested.description ?? ""),
			imageUrl: imagePath
				? resolveIntegratedMediaUrl(imagePath, sevenRooms)
				: "",
			applicable: parseFnBApplicableFor(row.applicable),
		};
	});
}

function formatAddOnPrice(row: Record<string, unknown>): string {
	if (
		typeof row.priceWithCurrency === "string" &&
		row.priceWithCurrency.trim()
	) {
		const formatted = row.priceWithCurrency.trim();
		if (/^(?:AED|USD|EUR|GBP)\s*0(?:\.0+)?$/i.test(formatted)) return "";
		if (/^0(?:\.0+)?\s*(?:AED|USD|EUR|GBP)$/i.test(formatted)) return "";
		return formatted;
	}
	const amount = String(
		row.priceAmount ?? row.price ?? row.amount ?? "",
	).trim();
	const currency = String(row.currency ?? "").trim();
	if (!amount || amount === "0" || Number(amount) === 0) return "";
	if (amount && currency) return `${currency} ${amount}`;
	return amount;
}

function mapAddOns(
	raw: unknown,
	options?: { sevenRooms?: boolean },
): RestaurantAddOn[] {
	if (!Array.isArray(raw)) return [];
	const sevenRooms = options?.sevenRooms ?? false;
	return raw.map((item, index) => {
		const row = (item ?? {}) as Record<string, unknown>;
		const imagePath = mapIntegratedMediaPath(row);
		return {
			id: String(row.id ?? `ad-${index}`),
			category: String(row.category ?? ""),
			name: String(row.title ?? row.name ?? ""),
			descriptionHtml: String(row.description ?? ""),
			priceWithCurrency: formatAddOnPrice(row),
			imageUrl: imagePath
				? resolveIntegratedMediaUrl(imagePath, sevenRooms)
				: "",
		};
	});
}

function resolveSevenRoomsRow(
	row: Record<string, unknown>,
): Record<string, unknown> {
	if (row.sevenRooms && typeof row.sevenRooms === "object") {
		return {
			...(row.sevenRooms as Record<string, unknown>),
			source: row.source,
			sevenRoomsId:
				(row.sevenRooms as Record<string, unknown>).sevenRoomsId ??
				row.sevenRoomsId,
			venueId:
				(row.sevenRooms as Record<string, unknown>).venueId ?? row.venueId,
		};
	}
	return row;
}

function mapStringTags(raw: unknown): string[] {
	if (!Array.isArray(raw)) return [];
	return raw.map((entry) => String(entry ?? "").trim()).filter(Boolean);
}

function mapPolicyDescriptions(raw: unknown): string {
	if (!Array.isArray(raw)) return "";
	return raw
		.map((entry) => {
			const policy = (entry ?? {}) as Record<string, unknown>;
			const type = String(policy.policyType ?? "").trim();
			const description = String(policy.policyDescription ?? "").trim();
			if (!description) return "";
			return type ? `${type}: ${description}` : description;
		})
		.filter(Boolean)
		.join("\n\n");
}

function mapLegacySevenRoomsPreferences(prefs: unknown): {
	specialOccasionTags: string[];
	allergyTags: string[];
} {
	const specialOccasionTags: string[] = [];
	const allergyTags: string[] = [];
	if (!Array.isArray(prefs)) {
		return { specialOccasionTags, allergyTags };
	}
	for (const pref of prefs) {
		const p = (pref ?? {}) as Record<string, unknown>;
		const category = String(p.category ?? p.type ?? "").toLowerCase();
		const label = String(p.label ?? p.name ?? p.value ?? "").trim();
		if (!label) continue;
		if (category.includes("occasion") || category.includes("special")) {
			specialOccasionTags.push(label);
		} else if (category.includes("allerg") || category.includes("diet")) {
			allergyTags.push(label);
		}
	}
	return { specialOccasionTags, allergyTags };
}

function mapGuestRange(row: Record<string, unknown>): {
	minGuest: number;
	maxGuest: number;
} {
	const minGuest = Number(row.minGuest ?? row.minGuests ?? 0) || 0;
	const maxGuest =
		Number(row.maxGuest ?? row.maxGuests ?? row.guestCountLimit ?? 0) || 0;
	return { minGuest, maxGuest };
}

function mapReservationWindowDays(value: unknown): number | null {
	if (value == null || value === "") return null;
	const days = Number(value);
	return Number.isFinite(days) ? days : null;
}

function mapSevenRooms(
	row: Record<string, unknown>,
): RestaurantSevenRoomsIntegration | null {
	if (mapSource(row.source) !== FnBRestaurantSourceType.SEVENROOMS) return null;
	const sr = resolveSevenRoomsRow(row);
	const sevenRoomsId = sr.sevenRoomsId;
	const venueId = sr.venueId;
	if (!sevenRoomsId && !venueId) return null;

	let specialOccasionTags = mapStringTags(row.specialOccasions);
	let allergyTags = mapStringTags(row.dietaryRestrictions);
	if (specialOccasionTags.length === 0 && allergyTags.length === 0) {
		const legacy = mapLegacySevenRoomsPreferences(sr.sevenRoomsPreferences);
		specialOccasionTags = legacy.specialOccasionTags;
		allergyTags = legacy.allergyTags;
	}

	const { minGuest, maxGuest } = mapGuestRange(row);
	const termsAndConditions =
		mapPolicyDescriptions(row.policy) || String(sr.termsAndConditions ?? "");

	return {
		sevenRoomsId: displayId(sevenRoomsId),
		venueId: displayId(venueId),
		reservationWindowDays: mapReservationWindowDays(sr.reservationWindowDays),
		minGuest,
		maxGuest,
		specialOccasionTags,
		allergyTags,
		termsAndConditions,
	};
}

export function mapFnBRestaurantListItem(raw: unknown): RestaurantListItem {
	const row = (raw ?? {}) as Record<string, unknown>;
	const propertyName =
		typeof row.propertyName === "string"
			? row.propertyName
			: typeof row.property === "object" &&
					row.property &&
					"name" in row.property
				? String((row.property as { name?: string }).name ?? "")
				: "";

	return {
		id: String(row.id ?? ""),
		name: String(row.name ?? ""),
		propertyId: String(row.propertyId ?? ""),
		propertyType: parseFnBPropertyType(row.propertyType),
		propertyName,
		source: mapSource(row.source),
		sevenRoomsId: displayId(row.sevenRoomsId),
		venueId: displayId(row.venueId),
		cuisineTags: Array.isArray(row.cuisineTags)
			? row.cuisineTags.map((t) => String(t))
			: [],
		status: mapPublicationStatus(row.publicationStatus ?? row.status),
		operationalStatus: String(row.status ?? ""),
		lastUpdatedBy: formatAuditUserLabel(row.lastUpdatedBy ?? row.updatedBy),
		lastUpdatedAt: String(row.lastUpdatedAt ?? row.updatedAt ?? ""),
	};
}

export function mapFnBRestaurantDetail(raw: unknown): RestaurantDetail {
	const row = (raw ?? {}) as Record<string, unknown>;
	const property =
		row.property && typeof row.property === "object"
			? (row.property as Record<string, unknown>)
			: {};
	const propertyId = String(row.propertyId ?? property.id ?? "");
	const propertyName = String(row.propertyName ?? property.name ?? "");
	const menu = mapMenuFields(row);
	const isSevenRooms =
		mapSource(row.source) === FnBRestaurantSourceType.SEVENROOMS;
	const propertyType = parseFnBPropertyType(row.propertyType);
	const media = mapMediaItems(row.bannerImage, row.media, {
		sevenRooms: isSevenRooms,
	});
	const cabanaMedia = mapMediaItems(row.cabanaBannerImage, row.cabanaMedia, {
		sevenRooms: isSevenRooms,
	});

	return {
		id: String(row.id ?? ""),
		propertyId,
		propertyType,
		name: String(row.name ?? ""),
		propertyName,
		source: mapSource(row.source),
		sevenRoomsId: displayId(row.sevenRoomsId),
		venueId: displayId(row.venueId),
		cuisineTags: Array.isArray(row.cuisineTags)
			? row.cuisineTags.map((t) => String(t))
			: [],
		status: mapPublicationStatus(row.publicationStatus ?? row.status),
		operationalStatus: String(row.status ?? ""),
		lastUpdatedBy: formatAuditUserLabel(
			row.lastUpdatedBy ?? row.updatedBy ?? row.createdBy,
		),
		lastUpdatedAt: String(
			row.lastUpdatedAt ?? row.updatedAt ?? row.createdAt ?? "",
		),
		phone: String(row.phoneNo ?? row.phone ?? ""),
		address: String(row.address ?? ""),
		mapUrl: String(row.googleLocation ?? row.mapUrl ?? ""),
		description: String(row.description ?? ""),
		media,
		cabanaMedia,
		faqs: mapFaqs(row.faqs, defaultFaqApplicable(propertyType)),
		...menu,
		sevenRooms: mapSevenRooms(row),
		seatingTypes: mapSeatingTypes(row.seatingTypes, {
			sevenRooms: isSevenRooms,
		}),
		addOns: mapAddOns(row.addOns, { sevenRooms: isSevenRooms }),
	};
}
